import { ACTIONS, STATUS, type EventHandler, type Step } from '@artic/ui/joyride';
import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * Guided "how to change partner" tour for Artic users. Demonstrates the flow —
 * it drives the shared NavUser dropdown / Change-Partner submenu / cmdk filter
 * through their controlled props via each step's `before` hook, and never
 * commits a real switch (the row step is highlighted only, and the global
 * `blockTargetInteraction` in `ArticTour` stops a stray click reloading the app).
 *
 * The step targets are `data-slot` attributes on the shared @artic/ui + cmdk
 * components — no purpose-built tour markers are needed.
 */
const SEEN_KEY = 'artic.quality.partnerTour.seen.v1';
/** Let a state change + Radix open/close animation settle before Joyride measures. */
const STEP_SETTLE_MS = 250;
/** Let the app paint after first load before the tour auto-starts. */
const AUTO_START_DELAY_MS = 600;

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

// The Radix trigger wins the asChild Slot merge, so the footer button carries
// data-slot="dropdown-menu-trigger" (not "sidebar-menu-button"). Scope to the
// footer so it's the one NavUser trigger, never another dropdown on the page.
const TRIGGER = '[data-slot="sidebar-footer"] [data-slot="dropdown-menu-trigger"]';
const CHANGE_PARTNER = '[data-slot="dropdown-menu-sub-trigger"]';
const SEARCH_INPUT = '[data-slot="command-input"]';
const ARTIC_PIN = '[data-slot="command-item"][data-value="pin:ART"]';

/** The first rendered partner row — after filtering to "Trunk", that's Trunk. */
function firstVisiblePartnerRow(): HTMLElement | null {
  const items = Array.from(document.querySelectorAll<HTMLElement>('[data-slot="command-item"]'));
  return items.find((el) => el.offsetParent !== null) ?? null;
}

/** Control props applied to the shared components only while the tour runs. */
export interface PartnerTourControls {
  run: boolean;
  steps: Step[];
  onEvent: EventHandler;
  startTour: () => void;
  navControl: { open: boolean; onOpenChange: () => void; modal: boolean } | undefined;
  changePartnerControl:
    | { open: boolean; onOpenChange: () => void; search: string; onSearchChange: () => void }
    | undefined;
}

// While the tour drives the menu, ignore Radix's own open/close + cmdk typing
// requests — the `before` hooks are the single source of truth for this state.
const noop = () => {};

export function usePartnerTour(enabled: boolean): PartnerTourControls {
  const [run, setRun] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [subOpen, setSubOpen] = useState(false);
  const [search, setSearch] = useState('');

  const endTour = useCallback(() => {
    setRun(false);
    setSubOpen(false);
    setMenuOpen(false);
    setSearch('');
  }, []);

  const startTour = useCallback(() => {
    setSubOpen(false);
    setMenuOpen(false);
    setSearch('');
    setRun(true);
  }, []);

  // Auto-start once per user (Artic users only). Guard localStorage: it throws
  // in some contexts (storage disabled, sandboxed iframe, private mode), and we
  // don't want that to break the effect. If we can't read the flag, skip the
  // auto-start (the "Take the tour" item still works); if we can't persist it,
  // run once anyway.
  useEffect(() => {
    if (!enabled) return;
    try {
      if (localStorage.getItem(SEEN_KEY)) return;
    } catch {
      return;
    }
    const timer = setTimeout(() => {
      try {
        localStorage.setItem(SEEN_KEY, '1');
      } catch {
        // Non-persistent storage — still run the tour once this session.
      }
      setRun(true);
    }, AUTO_START_DELAY_MS);
    return () => clearTimeout(timer);
  }, [enabled]);

  const steps = useMemo<Step[]>(
    () => [
      {
        target: TRIGGER,
        placement: 'right',
        title: 'Your account & partner menu',
        content:
          "This is your account menu. As an Artic user, it's also where you switch which partner's data you're viewing.",
        before: async () => {
          setSubOpen(false);
          setSearch('');
          setMenuOpen(false);
          await delay(STEP_SETTLE_MS);
        },
      },
      {
        target: CHANGE_PARTNER,
        placement: 'right',
        title: 'Change Partner',
        content: 'Open "Change Partner" to browse every partner you’re allowed to view.',
        before: async () => {
          setSearch('');
          setSubOpen(false);
          setMenuOpen(true);
          await delay(STEP_SETTLE_MS);
        },
      },
      {
        target: SEARCH_INPUT,
        placement: 'right',
        title: 'Search for a partner',
        content: 'Filter the list by name or key — here we’ve searched for "Trunk".',
        before: async () => {
          setMenuOpen(true);
          setSubOpen(true);
          setSearch('Trunk');
          await delay(STEP_SETTLE_MS);
        },
      },
      {
        target: firstVisiblePartnerRow,
        placement: 'right',
        title: 'Select a partner',
        content:
          "Click a result to switch to that partner's data. (We'll leave you where you are for this tour.)",
        before: async () => {
          setMenuOpen(true);
          setSubOpen(true);
          setSearch('Trunk');
          await delay(STEP_SETTLE_MS);
        },
      },
      {
        target: ARTIC_PIN,
        placement: 'right',
        title: 'Artic is always here',
        content:
          "Clear the search and Artic stays pinned at the very top — so you're always one click from switching back.",
        before: async () => {
          setMenuOpen(true);
          setSubOpen(true);
          setSearch('');
          await delay(STEP_SETTLE_MS);
        },
      },
    ],
    [],
  );

  const onEvent = useCallback<EventHandler>(
    (data) => {
      const { action, status } = data;
      // End on Done (FINISHED), Skip (SKIPPED), or ESC/close (CLOSE); normal
      // Next/Back keep the uncontrolled tour advancing on its own.
      if (status === STATUS.FINISHED || status === STATUS.SKIPPED || action === ACTIONS.CLOSE) {
        endTour();
      }
    },
    [endTour],
  );

  return {
    run,
    steps,
    onEvent,
    startTour,
    navControl: run ? { open: menuOpen, onOpenChange: noop, modal: false } : undefined,
    changePartnerControl: run
      ? { open: subOpen, onOpenChange: noop, search, onSearchChange: noop }
      : undefined,
  };
}
